import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-scroll-to-latest + "jump to latest" behaviour for the ticket Conversation
 * tab.
 *
 * The ticket detail page renders its conversation oldest-first (top → bottom),
 * which is the right reading order for a support thread — but it means the agent
 * lands on the OLDEST message and has to scroll past long email cards to reach
 * the newest one (the message they actually need to act on). This hook lands the
 * view on the newest message on open, and surfaces a floating "Latest" button
 * once the agent scrolls up into history.
 *
 * Three constraints shape the implementation:
 *  1. The scroll container is SHARED across all tabs (conversation/notes/history/
 *     line-items) and also wraps the AI panel + reply editor below the thread, so
 *     every effect is gated on `activeTab === 'conversation'`.
 *  2. Email bodies render in sandboxed iframes whose height is measured AFTER
 *     mount, so the content keeps GROWING for a few hundred ms after first paint.
 *     A one-shot scroll lands in the wrong place — we re-anchor via a
 *     ResizeObserver until the size settles.
 *  3. We must never yank the agent back down after they deliberately scroll up.
 */

export type ScrollBehaviorOpt = 'auto' | 'smooth';

interface UseScrollToLatestParams {
  /** Current ticket id (route param). Re-arms auto-scroll when it changes. */
  ticketId: string | undefined;
  /** Active tab; all behaviour is gated on `'conversation'`. */
  activeTab: string;
  /** Store loading flag — we wait for messages before arming. */
  isLoading: boolean;
  /**
   * Number of conversation messages currently rendered. Also gates the "Latest"
   * button: with a single message there is nothing to jump to, so the button is
   * suppressed regardless of how far a lone (long) email overflows the viewport.
   */
  messageCount: number;
}

interface UseScrollToLatestResult {
  /** Attach to the scrollable container (the shared `overflow-y-auto` div). */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  /** Wrap the conversation content (thread). Observed for async height growth. */
  conversationContentRef: React.RefObject<HTMLDivElement>;
  /** Attach to the container's `onScroll`. */
  onScroll: () => void;
  /**
   * Whether the floating "Latest" button should be shown. Only ever true when
   * the conversation has MORE THAN ONE message AND the newest is scrolled out of
   * view — a lone email (even a long one that overflows) has nothing later to
   * jump to, so the button stays hidden.
   */
  showLatest: boolean;
  /** Scroll so the newest message sits in view, reply box just below. */
  scrollToLatest: (behavior?: ScrollBehaviorOpt) => void;
  /**
   * Snap to the newest message AND re-engage the settle loop, so the view
   * tracks async content growth (e.g. an outbound reply that renders in a
   * resizing iframe). Use this after sending a message. Instant by design — a
   * smooth animation here would fire a storm of non-bottom scroll events that
   * the "user scrolled up" guard would mistake for a manual scroll.
   */
  trackLatest: () => void;
  /**
   * Count of messages that arrived while scrolled up. Always 0 today (the ticket
   * view has no live message feed); this is the seam for a future live count.
   */
  newCount: number;
}

/** Overscroll-bounce tolerance for "at bottom" / "has overflow" checks. */
const BOTTOM_THRESHOLD = 120;
/** Small gap left below the newest message so the reply box peeks in. */
const ANCHOR_BOTTOM_PADDING = 16;
/** No further resize for this long ⇒ content has settled, stop re-anchoring. */
const SETTLE_DEBOUNCE_MS = 300;

export function useScrollToLatest({
  ticketId,
  activeTab,
  isLoading,
  messageCount,
}: UseScrollToLatestParams): UseScrollToLatestResult {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const conversationContentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll is "armed" while we re-anchor to bottom as iframes settle.
  const armedRef = useRef(false);
  // Which ticket we've already auto-scrolled for — gives exactly one auto-scroll
  // per ticket open (and re-arms when the id changes).
  const handledTicketRef = useRef<string | null>(null);
  // True while WE are programmatically scrolling, so the resulting scroll event
  // isn't mistaken for the user scrolling up (which would disarm).
  const isProgrammaticRef = useRef(false);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  // Separate from rafRef (the onScroll visibility throttle): the one-shot
  // post-layout re-anchor scheduled by arm(). Kept distinct so neither clobbers
  // the other's pending frame.
  const anchorRafRef = useRef<number | null>(null);
  // Holds the latest `recomputeShowLatest` so `disarm` (whose identity must stay
  // stable — many effects depend on it) can run a FINAL visibility recompute once
  // the settle loop ends, without taking `recomputeShowLatest` as a dependency
  // (it changes with `activeTab`) or fighting the declaration order.
  const recomputeShowLatestRef = useRef<() => void>(() => {});

  const [showLatest, setShowLatest] = useState(false);
  const [newCount, setNewCount] = useState(0);

  const scrollToLatest = useCallback((behavior: ScrollBehaviorOpt = 'auto') => {
    const container = scrollContainerRef.current;
    const content = conversationContentRef.current;
    if (!container || !content) return;

    // Position the BOTTOM of the conversation content at the viewport bottom
    // (minus a small padding so the reply box peeks in just below). Computed via
    // getBoundingClientRect so it's correct regardless of offsetParent / any
    // positioned ancestor.
    const containerRect = container.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const contentBottomInContent =
      container.scrollTop + (contentRect.bottom - containerRect.top);
    const targetTop =
      contentBottomInContent - container.clientHeight + ANCHOR_BOTTOM_PADDING;

    isProgrammaticRef.current = true;
    container.scrollTo({ top: Math.max(0, targetTop), behavior });
    // Clear the guard after the (instant) scroll event has fired. Scroll events
    // dispatch before rAF callbacks, so the guard covers our own event.
    requestAnimationFrame(() => {
      isProgrammaticRef.current = false;
    });

    setNewCount(0);
  }, []);

  // `recompute` controls the final visibility recompute. It runs when the settle
  // genuinely ENDS (timer/resize-settle, user scroll-up, tab change, teardown) so
  // the button reflects the true settled bottom. arm() passes `false` for its
  // pre-re-arm teardown — it's about to scroll + re-arm, so an intermediate
  // measurement against the stale position would just flicker the button.
  const disarm = useCallback((recompute = true) => {
    const wasArmed = armedRef.current;
    armedRef.current = false;
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (settleTimerRef.current != null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    // Cancel a pending post-layout re-anchor so a user who scrolls up mid-settle
    // isn't yanked back to the bottom on the next frame.
    if (anchorRafRef.current != null) {
      cancelAnimationFrame(anchorRafRef.current);
      anchorRafRef.current = null;
    }
    // Settle is over: the view has landed on the true (post-layout) bottom. Now —
    // and ONLY now — compute the button visibility against the settled position.
    // recomputeShowLatest no-ops the button to hidden while armed, so this is the
    // first authoritative measurement after initial load / a post-send re-anchor.
    if (wasArmed && recompute) recomputeShowLatestRef.current();
  }, []);

  const arm = useCallback(() => {
    const content = conversationContentRef.current;
    if (!content) return;

    disarm(false); // clear any in-flight settle before re-arming (no flicker recompute)
    armedRef.current = true;

    // Instant initial scroll — covers the no-iframe / already-settled case.
    // (Always instant: see trackLatest doc — smooth here self-disarms.)
    scrollToLatest('auto');

    // The first scroll runs before the browser has laid out the just-mounted
    // thread (email iframes report height 0 until measured), so it lands SHORT of
    // the true bottom. Re-anchor on the next frame, after the first layout pass,
    // so we land at the latest message even when no ResizeObserver tick fires
    // (e.g. a plain-text thread with no async-growing content).
    if (anchorRafRef.current != null) cancelAnimationFrame(anchorRafRef.current);
    anchorRafRef.current = requestAnimationFrame(() => {
      anchorRafRef.current = null;
      if (armedRef.current) scrollToLatest('auto');
    });

    // Re-anchor as iframes/images grow the content height. ResizeObserver on the
    // fixed-height scroll container would never fire (its box doesn't change), so
    // we observe the conversation CONTENT wrapper, whose height grows with it.
    const observer = new ResizeObserver(() => {
      if (!armedRef.current) return;
      scrollToLatest('auto');
      if (settleTimerRef.current != null) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = window.setTimeout(disarm, SETTLE_DEBOUNCE_MS);
    });
    observer.observe(content);
    resizeObserverRef.current = observer;

    // Fallback disarm in case no resize ever fires.
    settleTimerRef.current = window.setTimeout(disarm, SETTLE_DEBOUNCE_MS);
  }, [disarm, scrollToLatest]);

  const recomputeShowLatest = useCallback(() => {
    const container = scrollContainerRef.current;
    const content = conversationContentRef.current;
    if (!container) return;
    if (activeTab !== 'conversation' || !content) {
      setShowLatest(false);
      return;
    }
    // While auto-scroll is armed we are actively re-anchoring to the bottom as
    // iframes/images settle, so the conversation is (or is becoming) at-bottom by
    // construction. Measuring now — before the async content has finished growing
    // and before the final re-anchor lands — reports a stale "not at bottom" and
    // flashes the button on initial load. Suppress it until settle disarms, at
    // which point disarm() runs a final recompute against the true bottom.
    if (armedRef.current) {
      setShowLatest((prev) => (prev === false ? prev : false));
      return;
    }
    // The scroll container is SHARED: below the conversation content it also holds
    // the AI panel + reply editor, which inflate scrollHeight. Measuring "at
    // bottom" against the container's scroll-bottom would therefore sit BELOW the
    // reply editor — so the button would stay on even when the newest message is
    // already in view. Measure against the CONVERSATION CONTENT's bottom edge
    // instead, mirroring scrollToLatest's content-relative anchor, so visibility
    // tracks the conversation alone, not the editor stacked beneath it.
    const containerRect = container.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    // How far the conversation's bottom edge sits below the visible viewport
    // bottom. <= 0 means the newest message is fully in view.
    const distanceFromContentBottom = contentRect.bottom - containerRect.bottom;
    const atBottom = distanceFromContentBottom <= BOTTOM_THRESHOLD;
    // Only meaningful when the conversation itself actually overflows the viewport.
    const contentHeight = contentRect.bottom - contentRect.top;
    const hasOverflow = contentHeight - container.clientHeight > BOTTOM_THRESHOLD;
    // A single message has no "later" message to jump to. A long lone email (big
    // signature + an embedded banner image) can still overflow the viewport, but
    // surfacing "Latest" there is pure noise — there is nothing below it to reach,
    // and it reads as a stray button floating over the email. Require at least two
    // messages before the button can appear.
    const hasJumpTarget = messageCount > 1;
    const next = hasJumpTarget && hasOverflow && !atBottom;
    setShowLatest((prev) => (prev === next ? prev : next));
  }, [activeTab, messageCount]);

  // Keep the ref pointed at the current recompute so disarm() (stable identity)
  // can fire a final, settled-position visibility recompute.
  recomputeShowLatestRef.current = recomputeShowLatest;

  const onScroll = useCallback(() => {
    // A genuine user scroll away from the bottom cancels the settle auto-stick —
    // never yank them back after they deliberately scroll up. Measured against the
    // conversation content's bottom (not the container's scroll-bottom, which sits
    // below the reply editor) so this matches recomputeShowLatest / scrollToLatest.
    if (!isProgrammaticRef.current && armedRef.current) {
      const container = scrollContainerRef.current;
      const content = conversationContentRef.current;
      if (container && content) {
        const distanceFromContentBottom =
          content.getBoundingClientRect().bottom -
          container.getBoundingClientRect().bottom;
        const atBottom = distanceFromContentBottom <= BOTTOM_THRESHOLD;
        if (!atBottom) disarm();
      }
    }
    // rAF-throttle the button-visibility recompute.
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      recomputeShowLatest();
    });
  }, [disarm, recomputeShowLatest]);

  // Arm exactly once per ticket open, once messages have loaded.
  useEffect(() => {
    if (activeTab !== 'conversation') return;
    if (isLoading || !ticketId || messageCount === 0) return;
    if (handledTicketRef.current === ticketId) return;
    // Wait for the conversation content to mount before claiming the one-shot,
    // so a not-yet-attached ref doesn't burn it (a later render will retry).
    if (!conversationContentRef.current) return;
    handledTicketRef.current = ticketId;
    arm();
  }, [activeTab, isLoading, ticketId, messageCount, arm]);

  // Keep the button state correct when switching tabs (no scroll event fires).
  useEffect(() => {
    recomputeShowLatest();
  }, [activeTab, recomputeShowLatest]);

  // New ticket while mounted → cancel any in-flight settle from the previous one.
  useEffect(() => {
    return () => disarm();
  }, [ticketId, disarm]);

  // Unmount cleanup.
  useEffect(() => {
    return () => {
      disarm();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [disarm]);

  return {
    scrollContainerRef,
    conversationContentRef,
    onScroll,
    showLatest,
    scrollToLatest,
    trackLatest: arm,
    newCount,
  };
}
